import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usersApi, rolesApi } from '@/services/api';
import type { SystemUserFormData } from '@/types/user';
import type { Role } from '@/types/roles';
import FormCard, { FormSection, FormGrid, FormField, FormInput } from '@/components/utils/FormCard';
import styles from './UserForm.module.css';
import { EMAIL_REGEX } from '@/utils/validators';

interface FormErrors {
  email?: string;
  first_name?: string;
  last_name?: string;
  general?: string;
}

const EMPTY_FORM: SystemUserFormData = {
  email: '',
  first_name: '',
  last_name: '',
  is_active: true,
  is_superuser: false,
  role_ids: [],
};

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<SystemUserFormData>(EMPTY_FORM);
  const [roles, setRoles] = useState<Role[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    rolesApi.getRoles()
      .then((data) => { if (!cancelled) setRoles(data); })
      .catch((err) => { if (!cancelled) console.error('Failed to load roles:', err); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isEditMode || !id) return;

    let cancelled = false;
    setLoadingData(true);

    usersApi.getUser(Number(id))
      .then((user) => {
        if (cancelled) return;
        setFormData({
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          is_active: user.is_active,
          is_superuser: user.is_superuser,
          role_ids: user.roles.map((r) => r.id),
        });
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Failed to load user data.');
          navigate('/users');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });

    return () => { cancelled = true; };
  }, [id, isEditMode, navigate]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!isEditMode && !formData.email?.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!isEditMode && formData.email && !EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required.';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof SystemUserFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, general: undefined }));
  };

  const handleRoleToggle = (roleId: number) => {
    setFormData((prev) => {
      const selected = prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((r) => r !== roleId)
        : [...prev.role_ids, roleId];
      return { ...prev, role_ids: selected };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (isEditMode && id) {
        const { email: _email, ...updateData } = formData;
        await usersApi.updateUser(Number(id), updateData);
        toast.success('User updated successfully.');
      } else {
        await usersApi.createUser(formData);
        toast.success('User created successfully.');
      }
      navigate('/users');
    } catch (err: unknown) {
      const responseData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      if (responseData && typeof responseData === 'object') {
        const fieldErrors: FormErrors = {};
        const pick = (v: unknown) => (Array.isArray(v) ? String(v[0]) : String(v));
        if (responseData.email) fieldErrors.email = pick(responseData.email);
        if (responseData.first_name) fieldErrors.first_name = pick(responseData.first_name);
        if (responseData.last_name) fieldErrors.last_name = pick(responseData.last_name);
        if (responseData.detail) fieldErrors.general = pick(responseData.detail);
        if (responseData.non_field_errors) fieldErrors.general = pick(responseData.non_field_errors);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      toast.error(isEditMode ? 'Failed to update user.' : 'Failed to create user.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = submitting
    ? (isEditMode ? 'Saving…' : 'Creating…')
    : (isEditMode ? 'Save Changes' : 'Create User');

  return (
    <FormCard
      title={isEditMode ? 'Edit User' : 'Add New User'}
      subtitle={isEditMode
        ? 'Update user details and role assignments.'
        : 'Create a new system user and assign roles.'}
      backLabel="← Back to Users"
      onBack={() => navigate('/users')}
      errorBanner={errors.general}
      submitLabel={submitLabel}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/users')}
      submitting={submitting}
      loading={loadingData}
      loadingMessage="Loading user data…"
      variant="page"
    >
      <FormSection title="Personal Information">
        <FormGrid>
          {!isEditMode && (
            <FormField label="Email Address" required error={errors.email}>
              <FormInput
                type="email"
                value={formData.email ?? ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="user@example.com"
                autoComplete="off"
                hasError={Boolean(errors.email)}
              />
            </FormField>
          )}

          <FormField label="First Name" required error={errors.first_name}>
            <FormInput
              type="text"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              placeholder="First name"
              hasError={Boolean(errors.first_name)}
            />
          </FormField>

          <FormField label="Last Name" required error={errors.last_name}>
            <FormInput
              type="text"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              placeholder="Last name"
              hasError={Boolean(errors.last_name)}
            />
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title="Access & Status">
        <div className={styles['toggle-row']}>
          <label className={styles['toggle-label']}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={formData.is_superuser}
              onChange={(e) => handleChange('is_superuser', e.target.checked)}
            />
            <span className={styles['toggle-text']}>
              <strong>Superuser</strong>
              <span className={styles['toggle-hint']}>Has all permissions, bypasses role checks</span>
            </span>
          </label>

          {isEditMode && (
            <label className={styles['toggle-label']}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              <span className={styles['toggle-text']}>
                <strong>Active</strong>
                <span className={styles['toggle-hint']}>Inactive users cannot log in</span>
              </span>
            </label>
          )}
        </div>
      </FormSection>

      <FormSection
        title="Role Assignment"
        description="Select one or more roles to define what this user can access. Superusers have full access regardless of roles."
      >
        {roles.length === 0 ? (
          <p className={styles['no-roles-msg']}>No roles available. Create roles in Roles Management first.</p>
        ) : (
          <div className={styles['roles-grid']}>
            {roles.map((role) => {
              const isSelected = formData.role_ids.includes(role.id);
              return (
                <label
                  key={role.id}
                  className={`${styles['role-card']} ${isSelected ? styles['role-card-selected'] : ''}`}
                >
                  <input
                    type="checkbox"
                    className={styles['role-checkbox']}
                    checked={isSelected}
                    onChange={() => handleRoleToggle(role.id)}
                  />
                  <span className={styles['role-name']}>{role.name}</span>
                  <span className={styles['role-scopes']}>
                    {role.scope_codenames.length} scope{role.scope_codenames.length !== 1 ? 's' : ''}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </FormSection>
    </FormCard>
  );
};

export default UserForm;